import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type {
  CreateTodoRequest,
  DoneOfferActionRequest,
  FellowUntieRequest,
  FellowUntieResponse,
  MeetingListResponse,
  ProjectRefDto,
  TodoDto,
  TodoFellowDto,
  TodoLinkChangesSeenRequest,
  TodoLinkChangesSeenResponse,
  TodoListResponse,
  TranscriptSegment,
  UpdateTodoRequest,
} from '../../shared/api-types.js';
import { TODO_NOTE_KIND_LABELS, TODO_PRIORITIES, TODO_SOURCE_LABELS, type TodoPriority, type TodoStatus } from '../../shared/enums.js';
import { api, type ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import {
  Banner,
  Button,
  Checkbox,
  Chip,
  ConfirmDialog,
  DataTable,
  Dialog,
  EM_DASH,
  FormErrorSummary,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  MenuItem,
  MenuPopover,
  ProjectChip,
  RelativeTime,
  SelectField,
  StatusPill,
  TextField,
  TextareaField,
  Tooltip,
  browserTimeZone,
  failureDetail,
  formatCount,
  formatDateOnly,
  inlineFailureText,
  pluralise,
  todayDateOnly,
  useIsMobile,
  useNow,
  useRequest,
  useMenu,
  useToast,
  useUrlSort,
  type DataTableColumn,
  type DataTableEmpty,
  type IconName,
  type MenuEntry,
  type RequestState,
  type SortParamOptions,
  type SortState,
} from '../ui/index.js';
import { refocusRow, rowPlace, type RowPlace } from '../ui/row-focus.js';
import {
  DONE_OFFER_DISMISSED,
  DONE_OFFER_FILTER_LABEL,
  FELLOW_GATE_TEXT,
  FELLOW_UNTIE_GATE_TEXT,
  PROJECT_NONE_CHIP_LABEL,
  PROJECT_NONE_PARAM_VALUE,
  PROJECT_UNKNOWN_CHIP_LABEL,
  SLACK_DAY_FILTER_LABEL,
  SLACK_DAY_LABEL,
  SLACK_DAY_PARAM_VALUE,
  TODO_PRIORITY_PRESENTATION,
  TODO_STATUS_FILTERS,
  TODO_STATUS_FILTER_LABELS,
  activeTodoFilterCount,
  clearedTodoFilters,
  colleagueDoneLine,
  doneOfferLine,
  doneOfferPath,
  filedUnderText,
  projectFilterEchoAction,
  projectFilterProject,
  todoSearchLabel,
  type ProjectFilterOption,
  fellowChipLabel,
  fellowChipName,
  fellowChipState,
  fellowItemPresentation,
  fellowLastWrittenLine,
  fellowLine,
  fellowTickOffExplanations,
  fellowTickOffGate,
  fellowUntieAnnouncement,
  fellowUntieDialog,
  fellowUntieGate,
  fellowUntieMenuLabel,
  fellowHereStatus,
  fellowKeptApart,
  isFellowHereAction,
  isTiedToFellow,
  showsFellowChip,
  tiedFellowItems,
  filterButtonCount,
  duePresentation,
  linkNoticeText,
  linkReasonLine,
  linkedChipName,
  linkedExplanation,
  linkedPeoplePhrase,
  meetingTodoSearch,
  newTodoValues,
  responsibleLabel,
  segmentIndexForOffset,
  statusChangeAnnouncement,
  todoDraftChanged,
  todoListPath,
  withTodoStatus,
  type FellowHereAction,
  type FellowLineAction,
  type FellowTickOffGate,
  type FellowUntieGate,
  type TodoDraft,
  type TodoFilters,
} from './lib/todos.js';
import { slackContextHref, slackTodayHref, todoHasWhy, todoWhyLink } from './lib/slack-context.js';
import { canOpenSlackToday } from './lib/assistant.js';
import { transcriptLinkText } from './lib/transcript-search.js';
import { ProjectCheckboxes } from './project-checkboxes.js';
import { ProjectChipList, ProjectFilingDialog, projectNames } from './project-filing.js';
import { ProjectFilterField } from './project-filter.js';
import './pages.css';

// The one to-do table (spec §13), rendered on the To-dos page and inside the meeting record page
// with the same behaviours: every column sorts with the sort in the URL, filters as removable chips,
// mark done / undo through PATCH with an optimistic row and an undoable toast (03-components §4.2
// prefers undo to confirm), edit in a dialog, dismiss, delete with a confirmation naming the to-do,
// and the four states plus filtered-empty. The page owns the filters (they live in its URL); this
// module owns the request, the mutations and the dialogs.

export const TODO_DEFAULT_SORT: SortState = { key: 'due_date', dir: 'asc' };

const TODO_TEXT_MAX = 500;

type TodoColumnKey = 'done' | 'text' | 'meeting' | 'project' | 'responsible' | 'priority' | 'due_date' | 'created_at' | 'actions';

const SORTABLE_KEYS: readonly TodoColumnKey[] = ['text', 'meeting', 'project', 'responsible', 'priority', 'due_date', 'created_at'];

/** The sort options a page shares with the table so both read the same URL state. */
export function todoSortOptions(prefix: string, options: { withMeeting: boolean; withProject?: boolean }): SortParamOptions<string> {
  const withProject = options.withProject ?? true;
  return {
    namespace: prefix ? prefix.replace(/_$/u, '') : undefined,
    keys: SORTABLE_KEYS.filter((key) => (options.withMeeting || key !== 'meeting') && (withProject || key !== 'project')),
    defaultSort: TODO_DEFAULT_SORT,
  };
}

export interface TodoListHandle {
  request: RequestState<TodoListResponse>;
  sort: SortState;
}

/**
 * Loads the list for the filters and the URL sort; the page calls this and hands the result to the table. A fixed
 * project (the project record page, ADR 0017) narrows every read to that project and drops the project column's sort,
 * as a fixed meeting drops the meeting's; with the projects floor off (`/api/me`) there is no project column to sort.
 */
export function useTodoList(filters: TodoFilters, options: { prefix?: string; fixedMeetingId?: string | null; fixedProjectId?: string | null } = {}): TodoListHandle {
  const prefix = options.prefix ?? '';
  const projectsOn = useMe().integrations.projects === 'on';
  const withProject = projectsOn && !options.fixedProjectId;
  const sortOptions = useMemo(() => todoSortOptions(prefix, { withMeeting: !options.fixedMeetingId, withProject }), [prefix, options.fixedMeetingId, withProject]);
  const [sort] = useUrlSort(sortOptions);
  const path = todoListPath(filters, sort, { fixedMeetingId: options.fixedMeetingId ?? null, fixedProjectId: options.fixedProjectId ?? null });
  const request = useRequest<TodoListResponse>((signal) => api.get<TodoListResponse>(path, { signal }), [path]);
  useRefetchOnReturn(request.reload);
  return { request, sort };
}

/** At most one refetch per this long when the person comes back to the tab. */
const RETURN_REFETCH_MS = 15_000;

/**
 * Lists refetch when the person comes back to the window (design §5.5): a colleague's tick may have
 * reached one of their linked to-dos meanwhile, and a page that still shows it open invites a change
 * the server would refuse as stale. The data on screen stays while it reloads (useRequest).
 */
function useRefetchOnReturn(reload: () => void): void {
  const last = useRef(Date.now());
  useEffect(() => {
    const onReturn = (): void => {
      if (document.visibilityState !== 'visible' || Date.now() - last.current < RETURN_REFETCH_MS) return;
      last.current = Date.now();
      reload();
    };
    window.addEventListener('focus', onReturn);
    document.addEventListener('visibilitychange', onReturn);
    return () => {
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [reload]);
}

/** Today's calendar date in the viewer's zone, refreshed each minute so "Due today" turns over at midnight. */
export function useToday(): string {
  const now = useNow(60_000);
  return useMemo(() => todayDateOnly(now, browserTimeZone()), [now]);
}

// Why `inlineFailureText` (the owner's ruling Q11 of escalation 20261005-200526): the to-do's Delete, Unlink and "Not the
// same…" dialogs set their error line here, and a refusal — the untie route's `fellow_connect` gate re-resolved after a
// grant is withdrawn — must name the pop-ups' next step, never the envelope's bare "You do not have permission to do this."
export function failureText(failure: ApiFailure): ReactNode {
  return (
    <>
      {inlineFailureText(failure)}
      {failure.correlationId ? <span className="text-xs muted"> Reference <code className="selectable">{failure.correlationId}</code></span> : null}
    </>
  );
}

// --- Cells -------------------------------------------------------------------------------------

export function PriorityPill({ priority }: { priority: TodoPriority }) {
  const meta = TODO_PRIORITY_PRESENTATION[priority];
  return <StatusPill tone={meta.tone} icon={meta.icon} title={`${meta.label} priority`}>{meta.label}</StatusPill>;
}

/** Relative wording with the absolute date in the title and the accessible name (04-data §4). */
export function DueDate({ dueDate, status, today }: { dueDate: string | null; status: TodoStatus; today: string }) {
  const due = duePresentation(dueDate, today, status);
  if (due.text === EM_DASH) return <span className="muted" aria-label="No due date">{EM_DASH}</span>;
  // Why `includes` and not inequality: past a week the wording is "Due 19 Oct 2026" — the absolute date already —
  // so only a genuinely relative reading ("Overdue by 7 days", "Due in 3 days") earns the absolute beside it.
  const relative = !due.text.includes(due.absolute);
  const label = relative ? `${due.text}, ${due.absolute}` : due.text;
  // Why the absolute is drawn as well as titled: a title is unreachable on touch (04-data §6.9).
  return (
    <span className={`due due--${due.tone}`} title={due.absolute}>
      {due.icon ? <Icon name={due.icon} size={14} /> : null}
      <span aria-hidden="true">
        {due.text}
        {relative ? <span className="due__absolute text-xs muted"> {due.absolute}</span> : null}
      </span>
      <span className="visually-hidden">{label}</span>
    </span>
  );
}

/**
 * Where a to-do came from. What the AI made from Slack, or across meetings (owner rulings A1, A6), wears a
 * chip — "From Slack", "Across meetings" — so it is told apart at a glance from the summary's to-dos and
 * the person's own, which keep the muted line.
 */
export function TodoSourceLabel({ todo }: { todo: Pick<TodoDto, 'source' | 'fromSweep'> }) {
  if (todo.source === 'slack' || todo.source === 'assistant') {
    const title =
      todo.source === 'assistant'
        ? 'Made by Calis from your recent meetings taken together'
        : todo.fromSweep
          ? 'Made by Calis from your daily look at Slack'
          : 'Made by Calis from what it found in Slack about this meeting';
    return (
      <Chip tone="accent" icon="sparkles" className="todo-source__chip" title={title}>
        {TODO_SOURCE_LABELS[todo.source]}
      </Chip>
    );
  }
  return <>{TODO_SOURCE_LABELS[todo.source]}</>;
}

/**
 * The Project column (ADR 0017; design §7.6): the to-do's projects as a cluster of label chips, each the way to its
 * record page, or the muted dash for a to-do in none. The name is the chip's text, so colour is never the only signal.
 */
export function TodoProjectsCell({ projects }: { projects: readonly ProjectRefDto[] }) {
  return <ProjectChipList projects={projects} className="todo-projects" />;
}

/** Where a note leads: a check's to its finding on the meeting's Slack tab; the daily sweep's to "Slack today", for a reader who can open it. */
function todoNoteHref(todo: Pick<TodoDto, 'meeting'>, note: TodoDto['notes'][number], slackToday: boolean): string | null {
  if (note.kind === 'slack') return todo.meeting !== null ? slackContextHref(todo.meeting.id, note.findingIds) : null;
  if (note.kind === 'sweep') return slackToday ? slackTodayHref(note.findingIds[0] ?? null) : null;
  return null;
}

/**
 * The notes under a to-do (owner ruling A3): one plain sentence each — "From Slack · Tamar says Alpine now
 * wants it by 28 Sep" — with the way to the finding behind it on the meeting's Slack tab, or, for what the daily
 * sweep noted, on the Assistant's "Slack today" tab when the reader can open it (`slackToday`). The to-do's own
 * words are never changed by a note.
 */
export function TodoNotes({ todo, slackToday = false }: { todo: Pick<TodoDto, 'text' | 'notes' | 'meeting'>; slackToday?: boolean }) {
  if (todo.notes.length === 0) return null;
  return (
    <ul className="todo-notes" aria-label={`What Slack shows about “${todo.text}”`}>
      {todo.notes.map((note) => {
        const href = todoNoteHref(todo, note, slackToday);
        return (
          <li key={note.id} className="todo-note text-sm">
            <span className="todo-note__text">
              <span className="secondary">{TODO_NOTE_KIND_LABELS[note.kind]} · </span>
              {note.text}
            </span>
            {href ? (
              // Why a standalone target: the glyph alone is the ruling's "›"; the name says where it goes.
              <Link to={href} className="todo-note__link hit-area">
                <span aria-hidden="true">›</span>
                <span className="visually-hidden">See what Slack shows about “{todo.text}”</span>
              </Link>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * "Why?" on a to-do the AI made (owner ruling A2): the one-sentence reason, and the way to what Slack
 * shows — or, for an item the brief made across meetings, to the brief — behind a disclosure — a busy person reads the to-do and opens the reason only when they want
 * to look further. A native disclosure: keyboard-operable, no hover needed, 44px on touch.
 */
export function TodoWhy({ todo, slackToday = false }: { todo: Pick<TodoDto, 'text' | 'source' | 'why' | 'meeting' | 'findingIds' | 'fromSweep'>; slackToday?: boolean }) {
  if (!todoHasWhy(todo, { slackToday })) return null;
  const link = todoWhyLink(todo, { slackToday });
  return (
    <details className="disclosure todo-why">
      <summary aria-label={`Why this to-do: ${todo.text}`}>
        <Icon name="chevron-down" size={16} />
        Why?
      </summary>
      <div className="disclosure__body stack stack--1">
        {todo.why ? <p className="todo-why__reason">{todo.why}</p> : null}
        {link ? (
          <p>
            <Link to={link.href} className="hit-area">
              {link.label}<span className="visually-hidden"> {link.kind === 'brief' ? 'for' : 'about'} “{todo.text}”</span> <span aria-hidden="true">›</span>
            </Link>
          </p>
        ) : null}
      </div>
    </details>
  );
}

/**
 * The "Linked" chip (ADR 0014; design §7; ruling Q8(a)): a real button after the source label that opens
 * a popover naming who the to-do is linked with — by name only when the colleague is on the reader's own
 * invite list — what a tick does, and Unlink…. Its name is in its accessible name and, on hover and on
 * focus, a tooltip; on touch the popover is the non-hover equivalent (01-foundations §6.4.3), a bottom
 * sheet on phones (03-components §5.1). Nothing of the colleague's copy is shown: the DTO carries none.
 */
export function LinkedChip({ todo, onUnlink }: { todo: TodoDto; onUnlink?: (todo: TodoDto) => void }) {
  const menu = useMenu();
  const link = todo.link;
  if (!link) return null;
  const name = linkedChipName(link);
  const explanation = linkedExplanation(link);
  const reason = linkReasonLine(todo);
  return (
    <>
      <Tooltip label={pluralise(link.others, { one: 'Ticks off with the linked copy', other: 'Ticks off with the linked copies' })}>
        {(anchor) => (
          <button
            {...menu.triggerProps}
            {...anchor}
            type="button"
            className="chip chip--neutral chip--button todo-linked hit-area"
            aria-label={name}
            onKeyDown={(event) => {
              anchor.onKeyDown(event);
              menu.triggerProps.onKeyDown(event);
            }}
          >
            <Icon name="link" size={14} />
            <span className="chip__label">Linked</span>
          </button>
        )}
      </Tooltip>
      <MenuPopover menu={menu} label={name} align="start" minWidth="18rem" className="todo-link-panel">
        <div className="todo-link-panel__body stack stack--1">
          <p className="todo-link-panel__who">{explanation.who}</p>
          <p className="secondary text-sm">{explanation.what}</p>
          {reason ? <p className="muted text-xs">{reason}</p> : null}
        </div>
        {onUnlink ? (
          <div role="menu" aria-label={name} className="menu">
            <MenuItem icon="unlink" onSelect={() => onUnlink(todo)}>Unlink…</MenuItem>
          </div>
        ) : null}
      </MenuPopover>
    </>
  );
}

/**
 * What the table lets a person do about a to-do's Fellow item: try a failed write again, take the one click a look
 * offered, or say an item Calis tied to the to-do is not the same work ("Not the same…", design §5.5).
 */
export interface TodoFellowActions {
  retry: (todo: TodoDto) => void;
  offer: (todo: TodoDto, action: 'tick_fellow' | 'reopen_fellow') => void;
  untie: (todo: TodoDto, item: TodoFellowDto['items'][number]) => void;
  /**
   * Stage 3's Undo, Tick off here and Reopen here: the person's own change of the to-do's status, sent exactly as the
   * checkbox sends it (with the status the row was drawn with, so a to-do that moved meanwhile is refused, 409). The
   * action is passed so the toast says what it was — following Fellow here, not a tick on its way to Fellow.
   */
  applyHere: (todo: TodoDto, to: TodoStatus, action: FellowHereAction) => void;
}

/** Where a to-do's Fellow item stands for this reader, and what they may do about it. */
export interface TodoFellowContext {
  /** The deployment's switch and the reader's grant, as /api/me reports them (selection only). */
  gate: FellowTickOffGate;
  /** Whether the reader may say a tied item is "Not the same…" (Fellow's floor and their Connect Fellow); `on` if not said. */
  untieGate?: FellowUntieGate;
  actions?: TodoFellowActions;
  /** A request about this to-do is in flight: its actions wait (03-components §2, loading). */
  busy?: boolean;
}

const FELLOW_ACTION_LABELS: Readonly<Record<FellowLineAction, string>> = {
  retry: 'Try again',
  tick_fellow: 'Tick off in Fellow',
  reopen_fellow: 'Reopen in Fellow',
  undo: 'Undo',
  tick_here: 'Tick off here',
  reopen_here: 'Reopen here',
};

const FELLOW_ACTION_ICONS = {
  retry: 'refresh',
  tick_fellow: 'check',
  reopen_fellow: 'undo',
  undo: 'undo',
  tick_here: 'check',
  reopen_here: 'undo',
} as const satisfies Record<FellowLineAction, IconName>;

function runFellowAction(actions: TodoFellowActions, todo: TodoDto, action: FellowLineAction): void {
  if (action === 'retry') {
    actions.retry(todo);
    return;
  }
  if (isFellowHereAction(action)) {
    // Why asked of the row again: the status the action needs is the row's as drawn; one that no longer applies sends nothing.
    const to = fellowHereStatus(todo, action);
    if (to !== null) actions.applyHere(todo, to, action);
    return;
  }
  actions.offer(todo, action);
}

/**
 * The Fellow chip (owner, 2026-09-30; design §9.6): on a `fellow` row it takes the place of the muted "Fellow" source
 * words; on one of the person's Calis to-dos that Calis tied a Fellow item to (Stage 2), it follows the to-do's own
 * source words as "In Fellow", and its menu offers "Not the same…" for each tied item. It is built like the Linked
 * chip — a real button, its state in a tooltip on hover and on focus and in its accessible name, a popover that is a
 * bottom sheet on phones (03-components §5.1) carrying everything the tooltip says (§5.2: no tooltips on touch). The
 * popover is an information body — Fellow's wording and state of each held item (and how a tied one matched), what is
 * on its way or did not get through, what came back from Fellow (Stage 3: "Completed in Fellow · date", "Done in
 * Fellow."), what a tick here does there — then, apart from it, a `role="menu"` list of the actions that apply (Try
 * again, Tick off in Fellow, Reopen in Fellow, Undo, Tick off here, Reopen here, Not the same…; F22: no information in
 * menu items). Its
 * four states (03-components §1): a failure with Try again; not available to you; off on this server; an item Fellow
 * gave no id (empty). Loading does not apply: the view comes with the row.
 */
export function FellowChip({ todo, gate, untieGate = 'on', actions, busy = false }: { todo: TodoDto } & TodoFellowContext) {
  const menu = useMenu();
  const fellow = todo.fellow;
  if (!fellow) return null;
  const name = fellowChipName(todo, gate);
  const state = fellowChipState(todo, gate);
  // Why the render's own clock: the popover is drawn when it opens, and the row's line keeps its own ticking clock.
  const line = fellowLine(todo, new Date(), gate);
  const failed = fellow.problem?.kind === 'failure';
  const gateText = gate !== 'on' && !(line?.text.includes(FELLOW_GATE_TEXT[gate]) ?? false) ? FELLOW_GATE_TEXT[gate] : null;
  const lastWritten = fellowLastWrittenLine(fellow);
  const action = line?.action && actions && !busy ? line.action : null;
  const reconnect = line?.reconnect === true && gate === 'on';
  const icon: IconName | null = failed ? 'alert-triangle' : fellow.pending !== null ? 'clock' : null;
  const tied = tiedFellowItems(fellow);
  // Why only while the reader may: the route refuses anyone else, and a control that can only fail is not offered — the
  // popover says why instead.
  const untie = actions && !busy && untieGate === 'on' ? actions.untie : null;
  const untieText = tied.length > 0 && untieGate !== 'on' ? FELLOW_UNTIE_GATE_TEXT[untieGate] : null;
  return (
    <>
      <Tooltip label={state}>
        {(anchor) => (
          <button
            {...menu.triggerProps}
            {...anchor}
            type="button"
            className={`chip ${failed ? 'chip--warning' : 'chip--neutral'} chip--button todo-fellow hit-area`}
            aria-label={name}
            onKeyDown={(event) => {
              anchor.onKeyDown(event);
              menu.triggerProps.onKeyDown(event);
            }}
          >
            {icon ? <Icon name={icon} size={14} /> : null}
            <span className="chip__label">{fellowChipLabel(todo)}</span>
          </button>
        )}
      </Tooltip>
      <MenuPopover menu={menu} label={name} align="start" minWidth="18rem" className="todo-link-panel todo-fellow-panel">
        <div className="todo-link-panel__body stack stack--1">
          <p className="todo-link-panel__who">{isTiedToFellow(todo) ? 'Also in Fellow’s action items' : 'From Fellow’s action items'}</p>
          <ul className="todo-fellow-items" role="list">
            {fellow.items.map((item, index) => {
              const shown = fellowItemPresentation(item, fellow.problem);
              return (
                <li key={index} className="todo-fellow-item">
                  <span className="todo-fellow-item__text">{shown.text ?? 'Fellow’s wording isn’t known'}</span>
                  {shown.match ? <span className="secondary text-sm">{shown.match}</span> : null}
                  <span className="secondary text-sm">{shown.state}</span>
                  {shown.note ? <span className="muted text-xs">{shown.note}</span> : null}
                </li>
              );
            })}
          </ul>
          {line ? (
            <p className={line.tone === 'warning' ? 'todo-fellow-status text-sm' : 'muted text-sm'}>
              {line.tone === 'warning' ? <Icon name="alert-triangle" size={14} className="todo-fellow-status__icon" /> : null}
              {line.text}
            </p>
          ) : null}
          {gate === 'on'
            ? fellowTickOffExplanations(fellow, Boolean(todo.link)).map((text) => <p key={text} className="secondary text-sm">{text}</p>)
            : gateText ? <p className="secondary text-sm">{gateText}</p> : null}
          {lastWritten ? <p className="muted text-xs">{lastWritten}</p> : null}
          {untieText ? <p className="secondary text-sm">{untieText}</p> : null}
        </div>
        {action || reconnect || (untie && tied.length > 0) ? (
          <div role="menu" aria-label={name} className="menu">
            {action && actions ? (
              <MenuItem icon={FELLOW_ACTION_ICONS[action]} onSelect={() => runFellowAction(actions, todo, action)}>{FELLOW_ACTION_LABELS[action]}</MenuItem>
            ) : null}
            {reconnect ? <MenuItem icon="plug" to={PATHS.connections}>Connect Fellow again</MenuItem> : null}
            {untie
              ? tied.map((item) => (
                <MenuItem key={item.ref} icon="unlink" onSelect={() => untie(todo, item)}>{fellowUntieMenuLabel(item, tied.length)}</MenuItem>
              ))
              : null}
          </div>
        ) : null}
      </MenuPopover>
    </>
  );
}

/**
 * The To-do column: the text, its source and state (and the Linked chip), an AI to-do's quote (on the
 * record page, where it can lead into the transcript), the notes under it, and "Why?". A `fellow` to-do's
 * source is the Fellow chip while there is something to say about Fellow (lib/todos.ts showsFellowChip); a
 * to-do Calis tied a Fellow item to keeps its own source words, and the "In Fellow" chip follows them.
 */
export function TodoTextCell({ todo, showQuote, transcriptLink, onUnlink, fellow }: {
  todo: TodoDto;
  showQuote: boolean;
  transcriptLink: string | null;
  onUnlink?: (todo: TodoDto) => void;
  fellow?: TodoFellowContext;
}) {
  const fellowContext: TodoFellowContext = fellow ?? { gate: 'on' };
  const fellowChip = showsFellowChip(todo, fellowContext.gate);
  const replacesSource = fellowChip && todo.source === 'fellow';
  return (
    <div className="todo-cell">
      <span className={['todo-text', todo.status === 'done' ? 'is-done' : '', todo.status === 'dismissed' ? 'is-dismissed' : ''].filter(Boolean).join(' ')}>{todo.text}</span>
      <span className="todo-source text-xs muted">
        {replacesSource ? <FellowChip todo={todo} {...fellowContext} /> : <TodoSourceLabel todo={todo} />}
        {fellowChip && !replacesSource ? <FellowChip todo={todo} {...fellowContext} /> : null}
        {todo.status === 'dismissed' ? <span>· Dismissed</span> : null}
        {todo.status === 'done' && todo.completedAt ? <span>· Completed <RelativeTime value={todo.completedAt} /></span> : null}
        {todo.link ? <LinkedChip todo={todo} {...(onUnlink ? { onUnlink } : {})} /> : null}
      </span>
      {showQuote && todo.sourceQuote ? (
        <blockquote className="todo-quote text-sm">
          <span aria-hidden="true">“</span>{todo.sourceQuote}<span aria-hidden="true">”</span>
          {transcriptLink ? (
            <>
              {' '}
              <Link to={transcriptLink} className="todo-quote__link">
                {transcriptLinkText(todo.sourceOffsetSeconds)}
              </Link>
            </>
          ) : null}
        </blockquote>
      ) : null}
    </div>
  );
}

/**
 * What sits under a to-do's row (04-data §1.12: a row of the same table, under the To-do column; on a
 * card, under the title): the notes and "Why?". Null for a to-do with neither, so no empty row is drawn.
 * Why a detail row and not the cell: a cell of stacked controls followed by a cell whose control sits at
 * the row's middle puts the later tab stop above the earlier one (01-foundations §7.6); beneath the row,
 * the reading order and the document order agree.
 */
export function TodoRowDetail({ todo, linkActions, fellow, offer, slackToday = false }: { todo: TodoDto; linkActions?: TodoLinkActions; fellow?: TodoFellowContext; offer?: TodoDoneOfferContext; slackToday?: boolean }) {
  const linkLine = <TodoLinkLine todo={todo} {...(linkActions ? { actions: linkActions } : {})} />;
  const hasLinkLine = linkReasonLine(todo) !== null || colleagueDoneLine(todo) !== null;
  const fellowContext: TodoFellowContext = fellow ?? { gate: 'on' };
  // Why a fixed clock for the decision only: whether a line shows does not depend on the time, only its words do.
  const hasFellowLine = fellowLine(todo, new Date(0), fellowContext.gate) !== null;
  const hasOfferLine = doneOfferLine(todo) !== null;
  if (todo.notes.length === 0 && !todoHasWhy(todo, { slackToday }) && !hasLinkLine && !hasFellowLine && !hasOfferLine) return null;
  return (
    <div className="todo-detail stack stack--1">
      {hasLinkLine ? linkLine : null}
      {hasOfferLine ? <TodoDoneOfferLine todo={todo} slackToday={slackToday} {...offer} /> : null}
      {hasFellowLine ? <TodoFellowLine todo={todo} {...fellowContext} /> : null}
      <TodoNotes todo={todo} slackToday={slackToday} />
      <TodoWhy todo={todo} slackToday={slackToday} />
    </div>
  );
}

/** What the table lets a person do from a linked to-do's reason line. */
export interface TodoLinkActions {
  setStatus: (todo: TodoDto, status: TodoStatus) => void;
  unlink: (todo: TodoDto) => void;
}

/**
 * The reason line under a linked to-do whose status came through its link (design §7; ADR 0014, S8): who ticked
 * it off or reopened it — or that Fellow marked it done, never said to be a colleague's tick — and when: the
 * reader's own copy's news, nothing of the colleague's. While that news is unseen, Undo and Unlink… stand beside
 * it, so the person the change reached can take it back or leave the link without hunting for the menu (the
 * notice's "Review" leads here). While the reader's copy is open and an active colleague's is done, the offer:
 * "<name> ticked off their copy" with "Tick off mine" (ruling Q6(a); `link.colleagueDone`, the name only when
 * the server gave one, ruling Q8(a)) — whether or not a reason line stands beside it, and whether or not it has
 * been seen. Beneath the row (04-data §1.12), so its controls follow the row's in document order
 * (01-foundations §7.6).
 */
export function TodoLinkLine({ todo, actions }: { todo: TodoDto; actions?: TodoLinkActions }) {
  const reason = linkReasonLine(todo);
  const offer = colleagueDoneLine(todo);
  if (!reason && !offer) return null;
  // Why the undo stands down while the offer shows: on an open copy both would tick it off — one button says it.
  const undo = reason && !offer && todo.link?.unseen && actions
    ? todo.status === 'done'
      ? { label: 'Reopen for everyone', to: 'open' as const }
      : { label: 'Tick off again', to: 'done' as const }
    : null;
  return (
    <p className="todo-link-line text-sm secondary">
      <Icon name="link" size={14} className="todo-link-line__icon" />
      <span className="todo-link-line__text">{[reason, offer].filter((part): part is string => part !== null).join(' · ')}</span>
      {undo && actions ? (
        <Button variant="link" size="sm" onClick={() => actions.setStatus(todo, undo.to)}>
          {undo.label}<span className="visually-hidden">: “{todo.text}”</span>
        </Button>
      ) : null}
      {offer && actions ? (
        <Button variant="link" size="sm" onClick={() => actions.setStatus(todo, 'done')}>
          Tick off mine<span className="visually-hidden">: “{todo.text}”</span>
        </Button>
      ) : null}
      {todo.link?.unseen && actions ? (
        <Button variant="link" size="sm" onClick={() => actions.unlink(todo)}>
          Unlink…<span className="visually-hidden"> “{todo.text}”</span>
        </Button>
      ) : null}
    </p>
  );
}

/** What a row's "Mark done" offer lets the person do (owner ruling Q8(b)); without it the line only says what Slack suggests. */
export interface TodoDoneOfferActions {
  accept: (todo: TodoDto) => void;
  dismiss: (todo: TodoDto) => void;
}

export interface TodoDoneOfferContext {
  actions?: TodoDoneOfferActions;
  busy?: boolean;
}

/**
 * The daily Slack sweep's suggestion that the to-do is done (owner ruling Q8(b), "One-click offer"; design §4): "Slack
 * suggests this is done — “reason”" with Mark done and Not done, and "See what Slack shows ›" for a reader who can open
 * Slack today (the evidence lives there, under the signal's id). Beside the link line, in its anatomy (04-data §1.12):
 * beneath the row, so its controls follow the row's in document order (01-foundations §7.6); each action names the
 * to-do it acts on, and keeps a 44px target on touch. Never a toast: the offer persists until decided or lapsed, so it
 * belongs on the row (03-components §6.2). Mark done is the person's own tick — the server ticks it through the
 * checkbox's own path — and nothing is ticked without the click.
 */
export function TodoDoneOfferLine({ todo, actions, busy = false, slackToday = false }: { todo: TodoDto; slackToday?: boolean } & TodoDoneOfferContext) {
  const text = doneOfferLine(todo);
  if (!text || !todo.doneOffer) return null;
  return (
    <p className="todo-link-line todo-offer-line text-sm secondary" data-done-offer={todo.doneOffer.id}>
      <Icon name="check-square" size={14} className="todo-link-line__icon" />
      <span className="todo-link-line__text">{text}</span>
      {actions ? (
        <>
          <Button variant="link" size="sm" loading={busy} onClick={() => actions.accept(todo)}>
            Mark done<span className="visually-hidden">: “{todo.text}”</span>
          </Button>
          <Button variant="link" size="sm" disabled={busy} onClick={() => actions.dismiss(todo)}>
            Not done<span className="visually-hidden">: “{todo.text}”</span>
          </Button>
        </>
      ) : null}
      {slackToday ? (
        <Link to={slackTodayHref(todo.doneOffer.signalId)} className="todo-link-line__link hit-area">
          See what Slack shows<span className="visually-hidden"> about “{todo.text}”</span> <span aria-hidden="true">›</span>
        </Link>
      ) : null}
    </p>
  );
}

/** How often the Fellow line re-reads the clock, so "Ticking off in Fellow…" turns into "Waiting…" after two minutes. */
const FELLOW_LINE_CLOCK_MS = 30_000;

/**
 * The line under a Fellow to-do's row (owner, 2026-09-30; design §9.6), stacked with the link line: a tick on its way
 * ("Ticking off in Fellow…", then "Waiting to tick off in Fellow"); a write that failed, as the warning status — glyph,
 * words and colour (01-foundations §1.5, §7.3) — with Try again, never only a toast; a change made in Fellow that Calis
 * applied here, with Undo, or offers to follow here, with Tick off here or Reopen here (Stage 3); the one click a look
 * offered; a reason Calis could not write it, muted. Success is quiet. Beneath the row (04-data §1.12), so its controls follow
 * the row's in document order (01-foundations §7.6).
 */
export function TodoFellowLine({ todo, gate, actions, busy = false }: { todo: TodoDto } & TodoFellowContext) {
  const now = useNow(FELLOW_LINE_CLOCK_MS);
  const line = fellowLine(todo, now, gate);
  if (!line) return null;
  const warning = line.tone === 'warning';
  return (
    <p className={['todo-fellow-line text-sm', warning ? 'todo-fellow-line--warning' : 'muted'].join(' ')}>
      <Icon name={warning ? 'alert-triangle' : todo.fellow?.pending ? 'clock' : 'info'} size={14} className="todo-fellow-line__icon" />
      <span className="todo-fellow-line__text">
        {warning ? <span className="visually-hidden">Warning: </span> : null}
        {line.text}
      </span>
      {line.action && actions ? (
        <Button variant="link" size="sm" loading={busy} onClick={() => runFellowAction(actions, todo, line.action!)}>
          {FELLOW_ACTION_LABELS[line.action]}<span className="visually-hidden">: “{todo.text}”</span>
        </Button>
      ) : null}
      {line.reconnect && gate === 'on' ? (
        <Link to={PATHS.connections} className="todo-fellow-line__link hit-area">
          Connect Fellow again<span className="visually-hidden"> to update “{todo.text}” there</span>
        </Link>
      ) : null}
    </p>
  );
}

/**
 * The notice above the table (design §7, F6): while changes that reached the reader's linked to-dos through
 * their links are unseen — colleagues' ticks and un-ticks, and completions Fellow reported — it says how many,
 * with Review (the "Changed through a link" view, where each row carries Undo and Unlink…) and Got it. A banner,
 * not a toast: the condition persists until seen
 * (03-components §6.2), and it is the control "the person the change reached sees it" rests on.
 */
export function LinkChangesNotice({ list, filters, onFiltersChange, fixedMeetingId, onSeen }: {
  list: TodoListHandle;
  filters: TodoFilters;
  onFiltersChange: (next: TodoFilters) => void;
  fixedMeetingId?: string;
  onSeen?: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const counts = list.request.data?.linkChanges ?? null;
  const text = linkNoticeText(counts, { inMeeting: Boolean(fixedMeetingId) });
  if (!text) return null;
  const gotIt = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    const body: TodoLinkChangesSeenRequest = fixedMeetingId ? { meetingId: fixedMeetingId } : {};
    const result = await api.post<TodoLinkChangesSeenResponse>('/api/todos/link-changes/seen', body);
    setBusy(false);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t mark those changes as seen. {failureDetail(result)}</>);
      return;
    }
    // Leaving the review with it: once seen, nothing is "changed through a link" any more.
    if (filters.changed) onFiltersChange({ ...filters, changed: false });
    list.request.reload();
    onSeen?.();
  };
  return (
    <Banner
      tone="info"
      icon="link"
      className="link-notice"
      actions={
        <>
          {filters.changed ? null : (
            <Button variant="secondary" size="sm" onClick={() => onFiltersChange({ ...filters, status: 'all', q: '', changed: true })}>
              Review<span className="visually-hidden"> the changed to-dos</span>
            </Button>
          )}
          <Button variant="ghost" size="sm" loading={busy} onClick={() => void gotIt()}>Got it</Button>
        </>
      }
    >
      {text}
    </Banner>
  );
}

// --- Filter bar --------------------------------------------------------------------------------

export interface MeetingOption {
  value: string;
  label: string;
}

export interface TodoFilterBarProps {
  filters: TodoFilters;
  onChange: (next: TodoFilters) => void;
  /** Meetings for the structured filter; omitted on the record page, where the meeting is fixed. */
  meetingOptions?: readonly MeetingOption[];
  hideMeetingFilter?: boolean;
  /**
   * The reader's own live projects for the Project filter and its chip (ADR 0017); null while they load or when they
   * could not be loaded (the select then waits, or says beside it that they failed — `projectOptionsFailure`; a chip still
   * names its project from the list's own answer).
   */
  projectOptions?: readonly ProjectFilterOption[] | null;
  /** The projects load's failure, when it failed: the select says so beside it, with a retry (Phase 1c D18). */
  projectOptionsFailure?: ApiFailure | null;
  onRetryProjectOptions?: () => void;
  /** The project record page: the project is fixed, so no project filter or chip. */
  hideProjectFilter?: boolean;
  /**
   * The list this bar narrows, for what its answer says of the project filter: a bookmarked id of a project since
   * merged away is rewritten to its survivor's, one that names nothing is dropped, and a status line says so.
   */
  list?: TodoListHandle | null;
  /** Result count line: matching rows and the unfiltered total, once known. */
  counts?: { matching: number; total: number } | null;
  /** Announced name for the count region. */
  noun?: { one: string; other: string };
}

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Search, status, meeting and project (03-components §10): the search names what it reaches, active filters
 * show as removable chips with Clear all, and below --bp-md the structured filters fold into a
 * sheet with an explicit Apply while the chips stay outside it (04-data §1.5).
 */
export function TodoFilterBar({ filters, onChange, meetingOptions = [], hideMeetingFilter = false, projectOptions = null, projectOptionsFailure = null, onRetryProjectOptions, hideProjectFilter = false, list = null, counts, noun = { one: '# to-do', other: '# to-dos' } }: TodoFilterBarProps) {
  const isMobile = useIsMobile();
  // The project filter exists only under the projects floor (owner ruling 9 of 2026-10-02; selection only — the API
  // ignores the parameter with the floor off and answers `project: null`).
  const projectsOn = useMe().integrations.projects === 'on';
  const showProjectFilter = projectsOn && !hideProjectFilter;
  const [draftQuery, setDraftQuery] = useState(filters.q);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<TodoFilters>(filters);
  // The status line the project echo left, and the filter value it explained — it stands while that value does.
  const [projectNotice, setProjectNotice] = useState<{ text: string; project: string | null } | null>(null);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Keep the box in step when the URL changes from elsewhere (a chip removal, Back).
  useEffect(() => {
    setDraftQuery(filters.q);
  }, [filters.q]);

  // Debounced search: one refetch per pause, not per keystroke.
  useEffect(() => {
    if (draftQuery.trim() === filtersRef.current.q) return;
    const handle = window.setTimeout(() => onChange({ ...filtersRef.current, q: draftQuery.trim() }), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [draftQuery, onChange]);

  // What the list's answer says of the project in the address (design §2.3, §7.7): an absorbed id is rewritten to its
  // survivor's — the chip already shows the survivor's name — and an id that names nothing drops the filter; either
  // way a status line says what happened, so a bookmark never lands on a quietly different set.
  const response = list?.request.data ?? null;
  const settled = list !== null && list.request.status === 'ready' && !list.request.refreshing;
  const knownIds = useMemo(() => (projectOptions ? projectOptions.map((option) => option.id) : null), [projectOptions]);
  const echo = useMemo(() => (response ? { project: response.project, resolvedFrom: response.resolvedFrom } : null), [response]);
  useEffect(() => {
    if (!showProjectFilter) return;
    const action = projectFilterEchoAction(filtersRef.current.project, echo, knownIds, settled);
    if (!action) return;
    const project = action.kind === 'rewrite' ? action.to.id : null;
    setProjectNotice({ text: action.notice, project });
    onChange({ ...filtersRef.current, project });
  }, [echo, knownIds, settled, showProjectFilter, onChange]);
  // Why the notice goes when the project filter changes by hand: it explained one address, not the next.
  useEffect(() => {
    setProjectNotice((current) => (current && current.project === filters.project ? current : null));
  }, [filters.project]);

  const activeCount = activeTodoFilterCount(filters, { ignoreMeeting: hideMeetingFilter, ignoreProject: !showProjectFilter });
  const structuredCount = filterButtonCount(filters, { ignoreMeeting: hideMeetingFilter, ignoreProject: !showProjectFilter });
  const meetingLabel = filters.meetingId ? meetingOptions.find((option) => option.value === filters.meetingId)?.label ?? 'Selected meeting' : null;
  const filterProject = projectFilterProject(filters.project, echo, projectOptions);
  const clearAll = (): void => onChange(clearedTodoFilters(filters, { keepMeeting: hideMeetingFilter }));

  const statusField = (value: TodoFilters['status'], set: (status: TodoFilters['status']) => void) => (
    <SelectField
      label="Status"
      value={value}
      onChange={(event) => set(event.target.value as TodoFilters['status'])}
      options={TODO_STATUS_FILTERS.map((status) => ({ value: status, label: TODO_STATUS_FILTER_LABELS[status] }))}
      className="filter-bar__status"
    />
  );
  // Why the Slack day among the meetings: the daily sweep's to-dos belong to no meeting (owner ruling Q5), and this is
  // where the list is narrowed by where a to-do came from. The two exclude each other, so one select holds both.
  const meetingField = (value: Pick<TodoFilters, 'meetingId' | 'fromSweep'>, set: (next: Pick<TodoFilters, 'meetingId' | 'fromSweep'>) => void) => (
    <SelectField
      label="Meeting"
      value={value.fromSweep && !value.meetingId ? SLACK_DAY_PARAM_VALUE : (value.meetingId ?? '')}
      onChange={(event) => {
        const chosen = event.target.value;
        set(chosen === SLACK_DAY_PARAM_VALUE ? { meetingId: null, fromSweep: true } : { meetingId: chosen || null, fromSweep: false });
      }}
      placeholderOption="Any meeting"
      options={[{ value: SLACK_DAY_PARAM_VALUE, label: `${SLACK_DAY_FILTER_LABEL} (no meeting)` }, ...meetingOptions]}
      className="filter-bar__meeting"
    />
  );
  // The Project filter (ADR 0017, design §7.7): every live own project by name, and "No project" for the to-dos filed
  // under none — and, when the projects failed to load, that failure beside it rather than a loading word (D18).
  const projectField = (value: string | null, set: (project: string | null) => void) => (
    <ProjectFilterField value={value} onChange={set} options={projectOptions} failure={projectOptionsFailure} onRetry={onRetryProjectOptions} />
  );
  // Why a link and not a button: the Projects page is where projects are renamed, merged and shared; this only leads there.
  const manageProjects = <LinkButton to={PATHS.projects} variant="link" size="sm" icon="folder">Manage projects</LinkButton>;

  const chips: ReactNode[] = [];
  if (filters.status !== 'open') {
    chips.push(
      <li key="status">
        <FilterChip label={`Status: ${TODO_STATUS_FILTER_LABELS[filters.status]}`} onRemove={() => onChange({ ...filters, status: 'open' })} />
      </li>,
    );
  }
  if (filters.meetingId && !hideMeetingFilter) {
    chips.push(
      <li key="meeting">
        <FilterChip label={`Meeting: ${meetingLabel ?? filters.meetingId}`} onRemove={() => onChange({ ...filters, meetingId: null })} />
      </li>,
    );
  }
  if (filters.fromSweep && !filters.meetingId && !hideMeetingFilter) {
    chips.push(
      <li key="slack-day">
        <FilterChip label={`Meeting: ${SLACK_DAY_FILTER_LABEL}`} onRemove={() => onChange({ ...filters, fromSweep: false })} />
      </li>,
    );
  }
  if (filters.project && showProjectFilter) {
    // The chip is the project's own label chip (design §7.6) so the word travels with its colour, named from the list's
    // answer first; "Project: none" and the not-yet-named case are plain chips.
    chips.push(
      <li key="project">
        {filters.project === PROJECT_NONE_PARAM_VALUE ? (
          <FilterChip label={PROJECT_NONE_CHIP_LABEL} onRemove={() => onChange({ ...filters, project: null })} />
        ) : filterProject ? (
          <ProjectChip project={filterProject} prefix="Project: " link={false} onRemove={() => onChange({ ...filters, project: null })} removeLabel="Remove filter: project" />
        ) : (
          <FilterChip label={PROJECT_UNKNOWN_CHIP_LABEL} onRemove={() => onChange({ ...filters, project: null })} />
        )}
      </li>,
    );
  }
  if (filters.q.trim() !== '') {
    chips.push(
      <li key="q">
        <FilterChip label={`Search: ${filters.q}`} onRemove={() => onChange({ ...filters, q: '' })} />
      </li>,
    );
  }
  if (filters.changed) {
    chips.push(
      <li key="changed">
        <FilterChip label="Changed through a link" onRemove={() => onChange({ ...filters, changed: false })} />
      </li>,
    );
  }
  if (filters.fellowFailed) {
    chips.push(
      <li key="fellow">
        <FilterChip label="Not ticked off in Fellow" onRemove={() => onChange({ ...filters, fellowFailed: false })} />
      </li>,
    );
  }
  if (filters.doneOffer) {
    chips.push(
      <li key="done-offer">
        <FilterChip label={DONE_OFFER_FILTER_LABEL} onRemove={() => onChange({ ...filters, doneOffer: false })} />
      </li>,
    );
  }

  const countLine = counts
    ? counts.matching !== counts.total
      ? `${formatCount(counts.matching)} of ${pluralise(counts.total, noun)}`
      : pluralise(counts.total, noun)
    : null;

  return (
    <div className="filter-bar stack stack--3">
      <div className="filter-bar__controls">
        <div className="filter-bar__search">
          <Icon name="search" size={16} className="filter-bar__search-icon" />
          <TextField
            label={todoSearchLabel(projectsOn)}
            type="search"
            inputMode="search"
            autoComplete="off"
            value={draftQuery}
            onChange={(event) => setDraftQuery(event.target.value)}
            className="filter-bar__search-field"
          />
          {draftQuery !== '' ? (
            <IconButton icon="x" label="Clear search" size="sm" className="filter-bar__search-clear" onClick={() => { setDraftQuery(''); onChange({ ...filters, q: '' }); }} />
          ) : null}
        </div>
        {isMobile ? (
          <Button
            variant="secondary"
            icon="filter"
            onClick={() => { setSheetDraft(filters); setSheetOpen(true); }}
            aria-haspopup="dialog"
          >
            {structuredCount > 0 ? `Filters (${structuredCount})` : 'Filters'}
          </Button>
        ) : (
          <>
            {statusField(filters.status, (status) => onChange({ ...filters, status }))}
            {hideMeetingFilter ? null : meetingField(filters, (next) => onChange({ ...filters, ...next }))}
            {showProjectFilter ? projectField(filters.project, (project) => onChange({ ...filters, project })) : null}
            {showProjectFilter ? manageProjects : null}
          </>
        )}
      </div>
      {chips.length > 0 || countLine ? (
        <div className="filter-bar__chips split">
          <ul className="cluster" role="list" aria-label="Active filters">
            {chips}
            {chips.length >= 2 ? (
              <li>
                <Button variant="link" size="sm" onClick={clearAll}>Clear all</Button>
              </li>
            ) : null}
          </ul>
          {countLine ? <p className="filter-bar__count text-sm secondary num" role="status" aria-live="polite">{countLine}</p> : null}
        </div>
      ) : null}
      {/* One visible status line, announced once (01-foundations §7.3): what the address's project id turned out to be. */}
      {showProjectFilter && projectNotice && projectNotice.project === filters.project ? (
        <p className="filter-bar__notice text-sm secondary" role="status" aria-live="polite">{projectNotice.text}</p>
      ) : null}
      <Dialog
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Filters"
        size="sm"
        initialFocus="first"
        actions={
          <>
            <Button variant="secondary" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => { onChange({ ...sheetDraft, q: filters.q }); setSheetOpen(false); }}>Apply</Button>
          </>
        }
      >
        <div className="stack stack--3">
          {statusField(sheetDraft.status, (status) => setSheetDraft((draft) => ({ ...draft, status })))}
          {hideMeetingFilter ? null : meetingField(sheetDraft, (next) => setSheetDraft((draft) => ({ ...draft, ...next })))}
          {showProjectFilter ? projectField(sheetDraft.project, (project) => setSheetDraft((draft) => ({ ...draft, project }))) : null}
          {showProjectFilter ? <div>{manageProjects}</div> : null}
        </div>
      </Dialog>
    </div>
  );
}

function FilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return <Chip tone="accent" selected onRemove={onRemove} removeLabel={`Remove filter: ${label}`}>{label}</Chip>;
}

// --- Table -------------------------------------------------------------------------------------

export interface TodoTableProps {
  caption: string;
  list: TodoListHandle;
  filters: TodoFilters;
  onFiltersChange: (next: TodoFilters) => void;
  /** Prefix of this table's URL params ('' on the To-dos page, 'todos_' on the record page). */
  prefix?: string;
  /** The URL param naming a row to highlight and scroll to ('highlight', or 'todo' on the record page). */
  highlightParam?: string;
  /** The record page: the meeting column is dropped and source quotes link into the transcript. */
  fixedMeetingId?: string;
  /** The project record page (ADR 0017): every read is that project's, and the project column is dropped. */
  fixedProjectId?: string;
  /**
   * Whether the header sticks against the page (the default, for a list page). A record page's embedded table passes
   * false: unpinned, its box scrolls horizontally where the page is narrower than the table (a 768px tablet), so the
   * page itself never scrolls sideways (02-patterns §1.1; 07-conformance T7).
   */
  stickyHeader?: boolean;
  /** Transcript segments, when loaded, so an AI to-do's quote can link to `#t-<index>`. */
  transcriptSegments?: readonly TranscriptSegment[] | null;
  empty: DataTableEmpty;
  /**
   * How many to-dos exist in this table's whole set, any status. The API's `total` counts every to-do
   * the user owns, so the record page passes the meeting's own count: "caught up" must mean this
   * meeting has to-dos and none are open, not that the person has to-dos somewhere else.
   */
  totalInSet?: number;
  /** Called after any successful mutation so a parent can refresh counts. */
  onMutated?: () => void;
}

/**
 * The controls a status change keeps keyboard focus on (Phase 1c finding D25; row-focus.ts): the done box's wrapper (the
 * Checkbox's `className`) and the box in it, and the row actions' trigger.
 */
const TODO_DONE_CELL = '.todo-done';
const TODO_DONE_BOX = '.todo-done input[type="checkbox"]';
const TODO_ACTIONS_CLASS = 'todo-actions';
const TODO_ACTIONS_TRIGGER = `.${TODO_ACTIONS_CLASS}`;

export function TodoTable({ caption, list, filters, onFiltersChange, prefix = '', highlightParam = 'highlight', fixedMeetingId, fixedProjectId, stickyHeader = true, transcriptSegments, empty, totalInSet, onMutated }: TodoTableProps) {
  const { request } = list;
  const toast = useToast();
  const today = useToday();
  const isMobile = useIsMobile();
  const [params, setParams] = useSearchParams();
  const highlightId = params.get(highlightParam);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<TodoDto | null>(null);
  // "File under…" (design §7.4): the row whose projects are being chosen.
  const [filing, setFiling] = useState<TodoDto | null>(null);
  const [deleting, setDeleting] = useState<TodoDto | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<ReactNode>(null);
  const [linkNotice, setLinkNotice] = useState<ReactNode>(null);
  const [unlinking, setUnlinking] = useState<TodoDto | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<ReactNode>(null);
  const [untying, setUntying] = useState<{ todo: TodoDto; item: TodoFellowDto['items'][number] } | null>(null);
  const [untieBusy, setUntieBusy] = useState(false);
  const [untieError, setUntieError] = useState<ReactNode>(null);
  const resolvingHighlight = useRef<string | null>(null);
  // Keeping keyboard focus through a status change (04-data §1.10.2 rule 4; Phase 1c finding D25; row-focus.ts): where
  // the change began — the row's place, and which of its controls to come back to — then whether its write settled and
  // the list's re-read has begun. Cleared once that re-read lands, or at once when the write failed: the box kept focus.
  const tableRoot = useRef<HTMLDivElement | null>(null);
  const keepFocus = useRef<{ place: RowPlace; selector: string; settled: boolean; reloading: boolean } | null>(null);
  const me = useMe();
  const fellowGate = fellowTickOffGate(me);
  const untieGate = fellowUntieGate(me);
  // Whether a sweep's to-do may lead to "Slack today": only for a reader who can open it (lib/assistant.ts).
  const slackToday = canOpenSlackToday(me);
  // The project column, the filing action and its dialog exist only under the projects floor (owner ruling 9).
  const projectsOn = me.integrations.projects === 'on';

  const items = request.data?.items ?? [];
  const withMeeting = !fixedMeetingId;
  const withProject = projectsOn && !fixedProjectId;

  const markBusy = useCallback((id: string, on: boolean) => {
    setBusy((current) => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const afterMutation = useCallback(() => {
    request.reload();
    onMutated?.();
  }, [request, onMutated]);

  // A to-do that moved since the page was drawn — a colleague's tick reached it through its link — is
  // refused by the server with 409 and nothing changes (design §5.5): the list is fetched again so the
  // person sees what now stands before they act again, and is told why.
  const onStale = useCallback((failure: ApiFailure): boolean => {
    if (failure.code !== 'conflict') return false;
    request.reload();
    toast.warning(failure.message);
    return true;
  }, [request, toast]);

  // Undo on a status change's toast: back to what the row said, refused (409) if it moved again meanwhile.
  const undoAction = useCallback((todo: TodoDto, undoTo: TodoStatus, landed: TodoStatus) => ({
    label: 'Undo',
    onClick: () => {
      void api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status: undoTo, expectedStatus: landed } satisfies UpdateTodoRequest).then((undo) => {
        if (!undo.ok) {
          if (!onStale(undo)) toast.error(<>We couldn&rsquo;t undo that. {failureDetail(undo)}</>);
          return;
        }
        afterMutation();
      });
    },
  }), [afterMutation, onStale, toast]);

  const setStatus = useCallback(async (todo: TodoDto, status: TodoStatus, options: { undoTo?: TodoStatus; here?: 'tick_here' | 'reopen_here' } = {}) => {
    const previous = todo.status;
    const optimisticAt = new Date().toISOString();
    // Why the place is taken now: a to-do that leaves the list shown (ticked in a list of open ones) is gone once the list
    // is read again, and with it the control that held focus. Which control: the box when the change began on it, else
    // the row's actions — a menu item hands focus back to its trigger as it closes.
    const place = tableRoot.current ? rowPlace(tableRoot.current, todo.id) : null;
    const startedOnBox = document.activeElement instanceof Element && document.activeElement.closest(TODO_DONE_CELL) !== null;
    keepFocus.current = place ? { place, selector: startedOnBox ? TODO_DONE_BOX : TODO_ACTIONS_TRIGGER, settled: false, reloading: false } : null;
    markBusy(todo.id, true);
    request.setData((current) => (current ? { ...current, items: withTodoStatus(current.items, todo.id, status, optimisticAt) } : current));
    const result = await api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status, expectedStatus: previous } satisfies UpdateTodoRequest);
    markBusy(todo.id, false);
    if (!result.ok) {
      // The row is put back where it stood and its box never lost focus (Checkbox `busy`), so a retry is one keystroke.
      if (keepFocus.current?.place.id === todo.id) keepFocus.current = null;
      request.setData((current) => (current ? { ...current, items: withTodoStatus(current.items, todo.id, previous, todo.updatedAt) } : current));
      if (!onStale(result)) toast.error(<>We couldn&rsquo;t update that to-do. {failureDetail(result)}</>);
      return;
    }
    const landed = result.data.status;
    // Why the answer's own status: a restored linked to-do takes its link's state (ADR 0014, S10), so it
    // may land ticked off though the person asked for open — through a colleague's tick, or as the person's
    // own when the shared done was theirs; the toast says which, naming no one. And the answer's own link, never
    // the row the page was drawn with: the server takes a person out of a link they no longer qualify for as it
    // applies their change, and a colleague may have gone inert meanwhile — then the answer's link is null and the
    // toast claims nothing crossed. Never a count of the copies the change reached: whether a tick closed a
    // colleague's copy says whether they had dismissed it (S10), which the page is never told (the sixth review of v2).
    // Why the answer's own Fellow view: only a write the server now has on its way is promised (F22) — never on the
    // switch, the grant or a connection the page cannot see.
    const announce = statusChangeAnnouncement({
      from: previous,
      asked: status,
      landed,
      landedVia: result.data.completedVia,
      link: result.data.link,
      fellowQueued: result.data.fellow?.pending === 'waiting',
      ...(options.here ? { here: options.here } : {}),
    });
    toast.success(announce, { key: `todo-${todo.id}`, action: undoAction(todo, options.undoTo ?? previous, landed) });
    if (keepFocus.current?.place.id === todo.id) keepFocus.current.settled = true;
    afterMutation();
  }, [afterMutation, markBusy, onStale, request, toast, undoAction]);

  // Once the list's re-read after a settled change lands: if the row left and took focus with it, focus goes to the same
  // control in the row now at its place (row-focus.ts). Why wait for the re-read to begin and then end: the write settles
  // with the row still drawn — the answer lands in the list only with the read that follows.
  useEffect(() => {
    const keep = keepFocus.current;
    if (!keep || !keep.settled) return;
    if (request.refreshing) {
      keep.reloading = true;
      return;
    }
    if (!keep.reloading) return;
    keepFocus.current = null;
    if (tableRoot.current) refocusRow(tableRoot.current, keep.place, keep.selector);
  }, [items, request.refreshing]);

  // Mark done and Not done under the daily sweep's suggestion (owner ruling Q8(b)). Mark done is the person's own
  // tick, made by the server through the checkbox's path, so its toast reads as the checkbox's does — what crossed to
  // linked copies and Fellow, from the answer — with the same Undo. A suggestion that no longer stands (409) or is no
  // longer there (404) reads the list again and says so; nothing was ticked.
  const decideOffer = useCallback(async (todo: TodoDto, action: 'accept' | 'dismiss') => {
    if (!todo.doneOffer) return;
    markBusy(todo.id, true);
    const result = await api.post<TodoDto>(doneOfferPath(todo.id, action), { offerId: todo.doneOffer.id } satisfies DoneOfferActionRequest);
    markBusy(todo.id, false);
    if (!result.ok) {
      if (!onStale(result)) {
        toast.error(<>{action === 'accept' ? 'We couldn’t mark that to-do done.' : 'We couldn’t turn that suggestion down.'} {failureDetail(result)}</>);
        request.reload();
      }
      return;
    }
    if (action === 'dismiss') {
      toast.success(DONE_OFFER_DISMISSED, { key: `todo-${todo.id}` });
      afterMutation();
      return;
    }
    const landed = result.data.status;
    const announce = statusChangeAnnouncement({ from: 'open', asked: 'done', landed, landedVia: result.data.completedVia, link: result.data.link, fellowQueued: result.data.fellow?.pending === 'waiting' });
    toast.success(announce, { key: `todo-${todo.id}`, action: undoAction(todo, 'open', landed) });
    afterMutation();
  }, [afterMutation, markBusy, onStale, request, toast, undoAction]);

  const offerActions = useMemo<TodoDoneOfferActions>(() => ({ accept: (todo) => void decideOffer(todo, 'accept'), dismiss: (todo) => void decideOffer(todo, 'dismiss') }), [decideOffer]);

  const toggleDone = useCallback((todo: TodoDto) => {
    void setStatus(todo, todo.status === 'done' ? 'open' : 'done');
  }, [setStatus]);

  const dismiss = useCallback((todo: TodoDto) => {
    void setStatus(todo, 'dismissed', { undoTo: todo.status });
  }, [setStatus]);

  const restore = useCallback((todo: TodoDto) => {
    void setStatus(todo, 'open');
  }, [setStatus]);

  const askUnlink = useCallback((todo: TodoDto) => {
    setUnlinkError(null);
    setUnlinking(todo);
  }, []);

  const linkActions = useMemo<TodoLinkActions>(() => ({ setStatus: (todo, status) => void setStatus(todo, status), unlink: askUnlink }), [setStatus, askUnlink]);

  // "Try again" and the one click a look offered (design §9.6): the server arms the write and queues the run, and
  // answers with the row as it now stands, which replaces the drawn one. Fellow itself is called only by the run.
  const fellowRequest = useCallback(async (todo: TodoDto, path: 'retry' | 'offer', body?: { action: 'tick_fellow' | 'reopen_fellow' }) => {
    markBusy(todo.id, true);
    const result = await api.post<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}/fellow/${path}`, body);
    markBusy(todo.id, false);
    if (!result.ok) {
      if (!onStale(result)) toast.error(<>We couldn&rsquo;t ask Fellow again. {failureDetail(result)}</>);
      return;
    }
    const saved = result.data;
    request.setData((current) => (current ? { ...current, items: current.items.map((item) => (item.id === saved.id ? saved : item)) } : current));
    const paused = saved.fellow?.pending === 'paused';
    const reopening = path === 'offer' ? body?.action === 'reopen_fellow' : saved.status !== 'done';
    toast.success(paused
      ? 'Saved. It goes to Fellow once your Fellow connection works again.'
      : reopening ? 'Reopening it in Fellow.' : 'Ticking it off in Fellow.');
    onMutated?.();
  }, [markBusy, onMutated, onStale, request, toast]);

  const askUntie = useCallback((todo: TodoDto, item: TodoFellowDto['items'][number]) => {
    setUntieError(null);
    setUntying({ todo, item });
  }, []);

  // Stage 3's Undo, Tick off here and Reopen here are the person's own change (design §7.3, §6.1): the checkbox's own
  // PATCH with the status the row was drawn with, its toast and its Undo — never a request about Fellow.
  const fellowActions = useMemo<TodoFellowActions>(() => ({
    retry: (todo) => void fellowRequest(todo, 'retry'),
    offer: (todo, action) => void fellowRequest(todo, 'offer', { action }),
    untie: askUntie,
    // Why no `here` for a "Reopen here" or "Tick off here" left by "Not the same…" (C5 and its mirror): the item was kept
    // apart, and "Reopened here, as in Fellow" or "Ticked off here, as in Fellow" would say what Fellow shows; it is the
    // person's own un-tick or tick, and its toast says what that does (linked copies, and Fellow when a write is on its way).
    applyHere: (todo, to, action) => void setStatus(todo, to, action === 'undo' || fellowKeptApart(todo) ? {} : { here: action }),
  }), [fellowRequest, askUntie, setStatus]);
  const fellowContext = useCallback(
    (todo: TodoDto): TodoFellowContext => ({ gate: fellowGate, untieGate, actions: fellowActions, busy: busy.has(todo.id) }),
    [fellowGate, untieGate, fellowActions, busy],
  );

  const confirmUnlink = async (): Promise<void> => {
    if (!unlinking) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    const result = await api.post<TodoDto>(`/api/todos/${encodeURIComponent(unlinking.id)}/unlink`);
    setUnlinkBusy(false);
    if (!result.ok) {
      setUnlinkError(failureText(result));
      return;
    }
    const others = unlinking.link?.others ?? 1;
    setUnlinking(null);
    toast.success(pluralise(others, { one: 'Unlinked. Your ticks no longer reach the other copy.', other: 'Unlinked. Your ticks no longer reach the other copies.' }));
    afterMutation();
  };

  // "Not the same…" (design §5.5): the answer carries the tied to-do as it now stands and the Fellow to-do the item
  // became, so the row is replaced and the new one shown beside it at once (or the person's restored one replaced); the
  // list is then fetched again in its own order and filters.
  const confirmUntie = async (): Promise<void> => {
    if (!untying) return;
    setUntieBusy(true);
    setUntieError(null);
    const result = await api.post<FellowUntieResponse>(
      `/api/todos/${encodeURIComponent(untying.todo.id)}/fellow/untie`,
      { item: untying.item.ref } satisfies FellowUntieRequest,
    );
    setUntieBusy(false);
    if (!result.ok) {
      setUntieError(failureText(result));
      return;
    }
    const { todo: kept, separated, reopening } = result.data;
    request.setData((current) => {
      if (!current) return current;
      const others = current.items.filter((item) => item.id !== separated.id);
      return { ...current, items: others.flatMap((item) => (item.id === kept.id ? [kept, separated] : [item])) };
    });
    setUntying(null);
    toast.success(fellowUntieAnnouncement(reopening));
    afterMutation();
  };

  const confirmDelete = async (): Promise<void> => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    const result = await api.delete<{ deleted: true; id: string }>(`/api/todos/${encodeURIComponent(deleting.id)}`);
    setDeleteBusy(false);
    if (!result.ok) {
      setDeleteError(failureText(result));
      return;
    }
    setDeleting(null);
    toast.success('To-do deleted');
    afterMutation();
  };

  // A filing — "File under…", or the edit dialog's Projects — is the person's sorting, not a change to the to-do
  // (owner ruling 2 of 2026-10-02, "No — it is sorting"): the row takes the answer, the toast names the projects and
  // offers Undo, which sends the previous set back (03-components §4.2 prefers undo to confirm). Why `expectedStatus`
  // is not sent: the server leaves `updated_at` alone for a filing, and the status it would check is not what changes.
  const announceFiling = useCallback((saved: TodoDto, previous: string[], options: { prefix?: string } = {}) => {
    const filed = filedUnderText(projectNames(saved.projects));
    const undo = {
      label: options.prefix ? 'Undo filing' : 'Undo',
      onClick: () => {
        void api.patch<TodoDto>(`/api/todos/${encodeURIComponent(saved.id)}`, { projects: previous } satisfies UpdateTodoRequest).then((result) => {
          if (!result.ok) {
            toast.error(<>We couldn&rsquo;t undo that filing. {failureDetail(result)}</>);
            return;
          }
          request.setData((current) => (current ? { ...current, items: current.items.map((item) => (item.id === result.data.id ? result.data : item)) } : current));
          afterMutation();
        });
      },
    };
    toast.success(options.prefix ? `${options.prefix} ${filed}.` : filed, { key: `todo-filing-${saved.id}`, action: undo });
  }, [afterMutation, request, toast]);

  // A deep link to a row that is not in the current view: find out why and either widen the
  // view to it or say so (06-modules §5.5 — never a silent landing at the top).
  const onHighlightMissing = useCallback((id: string) => {
    if (resolvingHighlight.current === id) return;
    resolvingHighlight.current = id;
    void api.get<TodoDto>(`/api/todos/${encodeURIComponent(id)}`).then((result) => {
      if (!result.ok) {
        setLinkNotice(result.code === 'not_found'
          ? 'The to-do you followed a link to no longer exists. It may have been deleted, or replaced when its summary was regenerated.'
          : `The to-do you followed a link to could not be checked: ${inlineFailureText(result)}`);
        return;
      }
      const todo = result.data;
      if (fixedMeetingId && todo.meeting?.id !== fixedMeetingId) {
        setLinkNotice(
          <>
            The to-do you followed a link to belongs to {todo.meeting ? <Link to={`${PATHS.meeting(todo.meeting.id)}${meetingTodoSearch(todo.id)}`}>{todo.meeting.title}</Link> : 'no meeting'}, not this one.
          </>,
        );
        return;
      }
      // The project page's addressing (design §7.3): a to-do filed elsewhere since the link was made is said so, never a quiet landing.
      if (fixedProjectId && !todo.projects.some((project) => project.id === fixedProjectId)) {
        setLinkNotice(
          <>
            That to-do is not in this project any more. <Link to={`${PATHS.todos}?highlight=${encodeURIComponent(todo.id)}`}>Open it in your to-do list</Link>.
          </>,
        );
        return;
      }
      if (filters.status !== 'all' && todo.status !== filters.status) {
        // Widening the view re-keys the table, which re-runs the highlight against the new rows.
        onFiltersChange({ ...filters, status: todo.status, q: '' });
        return;
      }
      setLinkNotice('The to-do you followed a link to is not in this list; the search, meeting or project filter may be hiding it.');
    });
  }, [filters, fixedMeetingId, fixedProjectId, onFiltersChange]);

  useEffect(() => {
    resolvingHighlight.current = null;
    setLinkNotice(null);
  }, [highlightId]);

  const clearHighlight = (): void => {
    setLinkNotice(null);
    setParams((current) => {
      const next = new URLSearchParams(current);
      next.delete(highlightParam);
      return next;
    }, { replace: true });
  };

  const transcriptLink = (todo: TodoDto): string | null => {
    if (!fixedMeetingId || !transcriptSegments) return null;
    const index = segmentIndexForOffset(transcriptSegments, todo.sourceOffsetSeconds);
    return index === null ? null : `${PATHS.meeting(fixedMeetingId)}?tab=transcript#t-${index}`;
  };

  // Folding (04-data §1.7; ADR 0168), declared once for the three lists this table is — the to-do list, a meeting's and a
  // project's: Added (scanning only) first, then where a to-do came from and is filed (Project, Meeting), then the values
  // a row is compared by, the default sort's Due last. The tick, the to-do's own words and its actions never fold.
  const columns = useMemo<DataTableColumn<TodoDto>[]>(() => {
    const all: DataTableColumn<TodoDto>[] = [
      {
        key: 'done',
        header: <span className="visually-hidden">Completed</span>,
        name: 'Completed',
        sortable: false,
        width: '2.5rem',
        card: 'actions',
        // Why the label shows on cards: the header that names the column is gone there (04-data §1.4).
        cell: (todo) => (
          <>
            <Checkbox
              label={isMobile
                ? <>{todo.status === 'done' ? 'Completed' : 'Mark as completed'}<span className="visually-hidden">: “{todo.text}”</span></>
                : <span className="visually-hidden">{todo.status === 'done' ? `Reopen “${todo.text}”` : `Mark “${todo.text}” as completed`}</span>}
              checked={todo.status === 'done'}
              // Why busy and not disabled (Phase 1c finding D25): a focused box that becomes disabled drops focus to the
              // document, so a keyboard user who ticked landed at the page's start. Busy keeps it, shows the write in flight
              // and refuses a second press until it settles.
              busy={busy.has(todo.id)}
              onChange={() => toggleDone(todo)}
              className="todo-done"
              {...(todo.link ? { 'aria-describedby': linkedTickId(todo.id) } : {})}
            />
            {/* What the box does to a linked to-do, read with it (design §7): the tick crosses. */}
            {todo.link ? <span id={linkedTickId(todo.id)} className="visually-hidden">{todo.status === 'done' ? 'Also reopens linked copies' : 'Also ticks off linked copies'}</span> : null}
          </>
        ),
      },
      {
        key: 'text',
        header: 'To-do',
        type: 'text',
        sortValue: (todo) => todo.text,
        card: 'title',
        cell: (todo) => <TodoTextCell todo={todo} showQuote={Boolean(fixedMeetingId)} transcriptLink={todo.sourceQuote ? transcriptLink(todo) : null} onUnlink={askUnlink} fellow={fellowContext(todo)} />,
      },
      {
        key: 'meeting',
        header: 'Meeting',
        type: 'text',
        // Why no value for a sweep's to-do: like every to-do without a meeting it sorts after the meetings, in the
        // server's order, which keeps "Your Slack day" together ahead of the rest (stores/todos.ts TODO_SORT_SQL).
        sortValue: (todo) => todo.meeting?.title ?? null,
        card: 'meta',
        fold: 3,
        cell: (todo) =>
          todo.meeting ? (
            <Link to={PATHS.meeting(todo.meeting.id)} className="todo-meeting hit-area">{todo.meeting.title}</Link>
          ) : todo.fromSweep ? (
            <span className="muted">{SLACK_DAY_LABEL}</span>
          ) : (
            <span className="muted">{EM_DASH}</span>
          ),
      },
      {
        key: 'project',
        header: 'Project',
        type: 'text',
        // Why the first name: the server orders by it too (stores/todos.ts, sort `project`, NULLs last), so the loaded
        // rows and the whole set agree; a to-do in no project sorts after the rest in either direction.
        sortValue: (todo) => todo.projects[0]?.name ?? null,
        card: 'meta',
        fold: 2,
        cell: (todo) => <TodoProjectsCell projects={todo.projects} />,
      },
      {
        key: 'responsible',
        header: 'Responsible',
        type: 'text',
        sortValue: (todo) => (responsibleLabel(todo) === EM_DASH ? null : responsibleLabel(todo)),
        card: 'field',
        fold: 4,
        cell: (todo) => {
          const label = responsibleLabel(todo);
          return label === EM_DASH ? <span className="muted" aria-label="Nobody named">{EM_DASH}</span> : label;
        },
      },
      {
        key: 'priority',
        header: 'Priority',
        type: 'enum',
        sortValue: (todo) => TODO_PRIORITY_PRESENTATION[todo.priority].rank,
        card: 'meta',
        fold: 5,
        cell: (todo) => <PriorityPill priority={todo.priority} />,
      },
      {
        key: 'due_date',
        header: 'Due',
        type: 'date',
        sortValue: (todo) => todo.dueDate,
        card: 'field',
        fold: 6,
        cell: (todo) => <DueDate dueDate={todo.dueDate} status={todo.status} today={today} />,
      },
      {
        key: 'created_at',
        header: 'Added',
        type: 'date',
        sortValue: (todo) => todo.createdAt,
        card: 'field',
        width: '8rem',
        fold: 1,
        cell: (todo) => <RelativeTime value={todo.createdAt} className="todo-added" />,
      },
      {
        key: 'actions',
        header: <span className="visually-hidden">Actions</span>,
        name: 'Actions',
        sortable: false,
        align: 'end',
        width: '3rem',
        card: 'actions',
        cell: (todo) => {
          const entries: MenuEntry[] = [
            { id: 'edit', label: 'Edit', icon: 'edit', onSelect: () => setEditing(todo) },
            // Filing by hand (ADR 0017, design §7.4): the same control the edit dialog carries, on its own.
            ...(projectsOn ? [{ id: 'file', label: 'File under…', icon: 'folder' as const, onSelect: () => setFiling(todo) }] : []),
            todo.status === 'done'
              // Why "for everyone": an un-tick of a linked to-do reopens every linked copy that is done (Q5(a); ADR 0014, S4).
              ? { id: 'reopen', label: todo.link ? 'Reopen for everyone' : 'Reopen', icon: 'undo', onSelect: () => toggleDone(todo) }
              : { id: 'done', label: 'Mark as completed', icon: 'check', onSelect: () => toggleDone(todo) },
            todo.status === 'dismissed'
              ? { id: 'restore', label: 'Restore', icon: 'undo', onSelect: () => restore(todo) }
              : { id: 'dismiss', label: 'Dismiss', icon: 'x', onSelect: () => dismiss(todo) },
            ...(todo.link ? [{ id: 'unlink', label: 'Unlink…', icon: 'unlink' as const, onSelect: () => askUnlink(todo) }] : []),
            { id: 'delete', label: 'Delete', icon: 'trash', danger: true, onSelect: () => { setDeleteError(null); setDeleting(todo); } },
          ];
          return (
            <Menu
              label={`Actions for “${todo.text}”`}
              entries={entries}
              trigger={(triggerProps) => {
                // Why busy and not disabled (Phase 1c finding D25): "Mark as completed" hands focus back to this trigger as
                // the row's write begins, and a disabled trigger would drop it to the document. Busy keeps it and shows the
                // write in flight; its presses are refused here until the write settles.
                const pending = busy.has(todo.id);
                return (
                  <IconButton
                    icon="more-horizontal"
                    label={`Actions for “${todo.text}”`}
                    className={TODO_ACTIONS_CLASS}
                    {...triggerProps}
                    {...(pending ? { onClick: undefined, onKeyDown: undefined, 'aria-disabled': true } : {})}
                    busy={pending}
                  />
                );
              }}
            />
          );
        },
      },
    ];
    return all.filter((column) => (withMeeting || column.key !== 'meeting') && (withProject || column.key !== 'project'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, today, isMobile, withMeeting, withProject, projectsOn, fixedMeetingId, transcriptSegments, toggleDone, dismiss, restore, askUnlink, fellowContext]);

  const filtersActive = activeTodoFilterCount(filters, { ignoreMeeting: Boolean(fixedMeetingId) }) > 0;
  const data = request.data;
  const truncated = data && data.items.length < data.matching;
  const status = request.status === 'loading'
    ? { kind: 'loading' as const, rowCount: 6 }
    : request.status === 'error' && request.error
      ? { kind: 'error' as const, failure: request.error, onRetry: request.reload }
      : { kind: 'ready' as const };

  // Nothing open, but the list is not empty: a different answer from "no to-dos yet".
  const caughtUp = data && (totalInSet ?? data.total) > 0 && data.matching === 0 && !filtersActive && filters.status === 'open';
  const emptyState: DataTableEmpty = caughtUp
    ? {
      title: 'Nothing open — you’re caught up',
      description: 'Every to-do is completed or dismissed.',
      action: <Button variant="secondary" onClick={() => onFiltersChange({ ...filters, status: 'done' })}>Show completed to-dos</Button>,
    }
    : empty;

  return (
    <div className="stack stack--3" ref={tableRoot}>
      <LinkChangesNotice list={list} filters={filters} onFiltersChange={onFiltersChange} {...(fixedMeetingId ? { fixedMeetingId } : {})} {...(onMutated ? { onSeen: onMutated } : {})} />
      {linkNotice ? (
        <Banner tone="info" onDismiss={clearHighlight} role="status">{linkNotice}</Banner>
      ) : null}
      <DataTable<TodoDto, string>
        stickyHeader={stickyHeader}
        key={`${prefix}${filters.status}`}
        caption={caption}
        failureNoun="the to-dos"
        columns={columns}
        rows={items}
        rowKey={(todo) => todo.id}
        status={status}
        sort={{ defaultSort: TODO_DEFAULT_SORT, namespace: prefix ? prefix.replace(/_$/u, '') : undefined }}
        clientSort
        empty={emptyState}
        filtered={{
          active: filtersActive,
          onClear: () => onFiltersChange(clearedTodoFilters(filters, { keepMeeting: Boolean(fixedMeetingId) })),
          title: 'No to-dos match these filters',
          description: 'Try a different status or search, or clear the filters to see every to-do.',
        }}
        highlightId={highlightId}
        onHighlightMissing={onHighlightMissing}
        rowClassName={(todo) => (todo.status === 'open' && duePresentation(todo.dueDate, today, todo.status).state === 'overdue' ? 'is-overdue' : undefined)}
        mobile="cards"
        rowDetail={(todo) => <TodoRowDetail todo={todo} linkActions={linkActions} fellow={fellowContext(todo)} offer={{ actions: offerActions, busy: busy.has(todo.id) }} slackToday={slackToday} />}
        detailFrom={1}
        footer={data && truncated ? (
          <p className="text-sm secondary num">
            {`Showing the first ${formatCount(data.items.length)} of ${formatCount(data.matching)} matching to-dos. Narrow the search to see the rest.`}
          </p>
        ) : null}
      />
      <TodoEditDialog
        todo={editing}
        onClose={() => setEditing(null)}
        onSaved={(saved, change) => {
          request.setData((current) => (current ? { ...current, items: current.items.map((item) => (item.id === saved.id ? saved : item)) } : current));
          setEditing(null);
          // Why two toasts' worth of words in one: a save that also refiled says both, and Undo takes back the filing
          // alone — the edit itself is the person's wording, which the dialog is for.
          if (change.projectsChanged) announceFiling(saved, change.previousProjectIds, change.fieldsChanged ? { prefix: 'To-do saved.' } : {});
          else toast.success('To-do saved');
          onMutated?.();
        }}
      />
      {filing ? (
        <ProjectFilingDialog<TodoDto>
          open
          title="File under…"
          description={<>The projects <strong>“{filing.text}”</strong> belongs to. Filing leaves the to-do itself untouched.</>}
          initial={filing.projects.map((project) => project.id)}
          onClose={() => setFiling(null)}
          onApply={(projects) => api.patch<TodoDto>(`/api/todos/${encodeURIComponent(filing.id)}`, { projects } satisfies UpdateTodoRequest)}
          onApplied={(saved, change) => {
            request.setData((current) => (current ? { ...current, items: current.items.map((item) => (item.id === saved.id ? saved : item)) } : current));
            setFiling(null);
            announceFiling(saved, change.previous);
            onMutated?.();
          }}
        />
      ) : null}
      <ConfirmDialog
        open={deleting !== null}
        title="Delete this to-do?"
        confirmLabel="Delete"
        danger
        busy={deleteBusy}
        error={deleteError}
        onCancel={() => { if (!deleteBusy) setDeleting(null); }}
        onConfirm={confirmDelete}
      >
        <p>“{deleting?.text}” will be deleted for good. Dismiss it instead if you may want it back.</p>
        {/* Why said here (design §5.9): deleting lets go of Fellow's item — Calis neither ticks it off there nor imports it again. */}
        {deleting?.fellow ? <p>Fellow&rsquo;s item stays as it is in Fellow; Calis won&rsquo;t tick it off there, nor import it again.</p> : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={untying !== null}
        title={fellowUntieDialog(untying?.item ?? { closedByCalis: false }, fellowGate).title}
        confirmLabel={fellowUntieDialog(untying?.item ?? { closedByCalis: false }, fellowGate).confirmLabel}
        busy={untieBusy}
        error={untieError}
        onCancel={() => { if (!untieBusy) setUntying(null); }}
        onConfirm={confirmUntie}
      >
        {untying ? (
          <>
            <p>{fellowUntieDialog(untying.item, fellowGate, { ...untying.todo, stateShown: me.integrations.fellowState === 'on' && me.capabilities.fellowConnect }).body}</p>
            <p className="secondary">
              This to-do: “{untying.todo.text}”. Fellow&rsquo;s item: “{untying.item.text ?? 'its wording isn’t known'}”.
            </p>
            <p>{fellowUntieDialog(untying.item, fellowGate).lasting}</p>
          </>
        ) : null}
      </ConfirmDialog>
      <ConfirmDialog
        open={unlinking !== null}
        title="Unlink this to-do?"
        confirmLabel="Unlink"
        danger
        busy={unlinkBusy}
        error={unlinkError}
        onCancel={() => { if (!unlinkBusy) setUnlinking(null); }}
        onConfirm={confirmUnlink}
      >
        <p>
          “{unlinking?.text}” will no longer be linked with {unlinking?.link ? linkedPeoplePhrase(unlinking.link) : 'a colleague'}. Ticking off one copy
          will no longer tick off the others, and Calis will not link it again. Your to-do and its state stay as they are.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/** The id of the hidden line that tells a screen reader what a linked to-do's box does. */
function linkedTickId(todoId: string): string {
  return `todo-linked-tick-${todoId}`;
}

// --- Dialogs -----------------------------------------------------------------------------------

interface TodoFormValues {
  text: string;
  dueDate: string;
  priority: TodoPriority;
  meetingId: string;
}

interface TodoFormErrors {
  text?: string;
  dueDate?: string;
  priority?: string;
  meetingId?: string;
  summary: string[];
}

function validate(values: TodoFormValues): TodoFormErrors {
  const errors: TodoFormErrors = { summary: [] };
  const text = values.text.trim();
  if (text === '') errors.text = 'Write what needs doing.';
  else if (text.length > TODO_TEXT_MAX) errors.text = `Keep the to-do under ${formatCount(TODO_TEXT_MAX)} characters.`;
  if (values.dueDate !== '' && !/^\d{4}-\d{2}-\d{2}$/u.test(values.dueDate)) errors.dueDate = 'Enter the due date as a calendar date.';
  errors.summary = [errors.text, errors.dueDate].filter((message): message is string => Boolean(message));
  return errors;
}

/** Field errors the server names take its message; anything else goes to the summary. */
function fromFailure(failure: ApiFailure): TodoFormErrors {
  const fields = new Set(failure.fields ?? []);
  const errors: TodoFormErrors = { summary: [] };
  if (fields.has('text')) errors.text = failure.message;
  if (fields.has('dueDate')) errors.dueDate = failure.message;
  if (fields.has('priority')) errors.priority = failure.message;
  if (fields.has('meetingId')) errors.meetingId = failure.message;
  if (fields.size === 0) errors.summary = [failure.correlationId ? `${inlineFailureText(failure)} (reference ${failure.correlationId})` : inlineFailureText(failure)];
  return errors;
}

const PRIORITY_OPTIONS = TODO_PRIORITIES.map((priority) => ({ value: priority, label: TODO_PRIORITY_PRESENTATION[priority].label }));

function TodoFields({ values, errors, onChange, meetingOptions, meetingsLoading }: {
  values: TodoFormValues;
  errors: TodoFormErrors;
  onChange: (next: Partial<TodoFormValues>) => void;
  /** Present on the To-dos page only; the record page fixes the meeting. */
  meetingOptions?: readonly MeetingOption[] | null;
  meetingsLoading?: boolean;
}) {
  return (
    <div className="stack stack--3">
      <TextareaField
        label="To-do"
        required
        value={values.text}
        onChange={(event) => onChange({ text: event.target.value })}
        error={errors.text}
        maxLength={TODO_TEXT_MAX}
        rows={3}
        autoComplete="off"
      />
      <TextField
        label="Due date"
        type="date"
        value={values.dueDate}
        onChange={(event) => onChange({ dueDate: event.target.value })}
        error={errors.dueDate}
        autoComplete="off"
      />
      <SelectField
        label="Priority"
        value={values.priority}
        onChange={(event) => onChange({ priority: event.target.value as TodoPriority })}
        options={PRIORITY_OPTIONS}
        error={errors.priority}
      />
      {meetingOptions ? (
        <SelectField
          label="Meeting"
          value={values.meetingId}
          onChange={(event) => onChange({ meetingId: event.target.value })}
          placeholderOption={meetingsLoading ? 'Loading meetings…' : 'Not linked to a meeting'}
          options={meetingOptions}
          error={errors.meetingId}
          disabled={meetingsLoading}
        />
      ) : null}
    </div>
  );
}

/** What the edit dialog changed, so the table's toast can say it and Undo can take the filing back. */
export interface TodoEditChange {
  /** The to-do's own fields — text, due date, priority — changed. */
  fieldsChanged: boolean;
  /** The person's projects for it changed (ADR 0017): a filing, which leaves the to-do itself untouched. */
  projectsChanged: boolean;
  previousProjectIds: string[];
}

function sameIds(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  return b.every((id) => left.has(id));
}

export function TodoEditDialog({ todo, onClose, onSaved }: { todo: TodoDto | null; onClose: () => void; onSaved: (saved: TodoDto, change: TodoEditChange) => void }) {
  // The Projects fieldset exists only under the projects floor (owner ruling 9); the PATCH never names `projects` without it.
  const projectsOn = useMe().integrations.projects === 'on';
  const [values, setValues] = useState<TodoFormValues>({ text: '', dueDate: '', priority: 'medium', meetingId: '' });
  const [projects, setProjects] = useState<string[]>([]);
  const [errors, setErrors] = useState<TodoFormErrors>({ summary: [] });
  const [saving, setSaving] = useState(false);
  const summaryRef = useRef<HTMLFormElement | null>(null);

  useEffect(() => {
    if (todo) {
      setValues({ text: todo.text, dueDate: todo.dueDate ?? '', priority: todo.priority, meetingId: todo.meeting?.id ?? '' });
      setProjects(todo.projects.map((project) => project.id));
      setErrors({ summary: [] });
      setSaving(false);
    }
  }, [todo]);

  const heldProjects = useMemo(() => (todo ? todo.projects.map((project) => project.id) : []), [todo]);
  const fieldsDirty = todo !== null && (values.text !== todo.text || values.dueDate !== (todo.dueDate ?? '') || values.priority !== todo.priority);
  const projectsDirty = todo !== null && projectsOn && !sameIds(projects, heldProjects);
  const dirty = fieldsDirty || projectsDirty;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!todo) return;
    const checked = validate(values);
    if (checked.summary.length > 0) {
      setErrors(checked);
      window.setTimeout(() => summaryRef.current?.querySelector<HTMLElement>('[role="alert"]')?.focus(), 0);
      return;
    }
    const body: UpdateTodoRequest = {};
    if (values.text.trim() !== todo.text) body.text = values.text.trim();
    if (values.dueDate !== (todo.dueDate ?? '')) body.dueDate = values.dueDate === '' ? null : values.dueDate;
    if (values.priority !== todo.priority) body.priority = values.priority;
    // Why only when changed: a filing alone leaves `updated_at` untouched on the server (owner ruling 2); sending the
    // same set would still be a filing, and a text edit must not be read as refiling.
    if (projectsDirty) body.projects = projects;
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    setSaving(true);
    const result = await api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, body);
    setSaving(false);
    if (!result.ok) {
      setErrors(fromFailure(result));
      return;
    }
    onSaved(result.data, { fieldsChanged: body.text !== undefined || body.dueDate !== undefined || body.priority !== undefined, projectsChanged: projectsDirty, previousProjectIds: heldProjects });
  };

  return (
    <Dialog
      open={todo !== null}
      onClose={onClose}
      title="Edit to-do"
      size="md"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" form="todo-edit-form" loading={saving}>Save changes</Button>
        </>
      }
    >
      <form id="todo-edit-form" onSubmit={(event) => void submit(event)} noValidate ref={summaryRef} className="stack stack--3">
        <FormErrorSummary errors={errors.summary} />
        <TodoFields values={values} errors={errors} onChange={(next) => setValues((current) => ({ ...current, ...next }))} />
        {projectsOn && todo ? <ProjectCheckboxes selected={projects} onChange={setProjects} disabled={saving} description="Filing leaves the to-do itself untouched." /> : null}
      </form>
    </Dialog>
  );
}

export function AddTodoDialog({ open, onClose, fixedMeetingId, initialProjectIds, initial, onCreated }: {
  open: boolean;
  onClose: () => void;
  fixedMeetingId?: string;
  /** Projects ticked when the form opens (the project record page files the new to-do under itself); still the person's choice. */
  initialProjectIds?: readonly string[];
  /** Open filled in with a suggestion to review and edit, rather than blank; it is still the person's own to-do. */
  initial?: TodoDraft;
  onCreated: (created: TodoDto) => void;
}) {
  const projectsOn = useMe().integrations.projects === 'on';
  const [values, setValues] = useState<TodoFormValues>({ ...newTodoValues(initial), meetingId: fixedMeetingId ?? '' });
  const [projects, setProjects] = useState<string[]>([...(initialProjectIds ?? [])]);
  const [errors, setErrors] = useState<TodoFormErrors>({ summary: [] });
  const [saving, setSaving] = useState(false);
  const summaryRef = useRef<HTMLFormElement | null>(null);
  // Why: the meeting list is only needed for the optional link, and only while the dialog is open.
  const meetings = useRequest<MeetingListResponse>(
    (signal) => (open && !fixedMeetingId ? api.get<MeetingListResponse>('/api/meetings?sort=started_at&dir=desc', { signal }) : new Promise<never>(() => {})),
    [open, fixedMeetingId],
  );

  // Why the draft's fields and not the object: a parent re-rendering with an equal draft must not wipe
  // what the person has typed since the dialog opened.
  const initialText = initial?.text;
  const initialPriority = initial?.priority;
  const initialProjectsKey = (initialProjectIds ?? []).join(',');
  useEffect(() => {
    if (open) {
      const draft = initialText === undefined ? undefined : { text: initialText, priority: initialPriority };
      setValues({ ...newTodoValues(draft), meetingId: fixedMeetingId ?? '' });
      setProjects(initialProjectsKey === '' ? [] : initialProjectsKey.split(','));
      setErrors({ summary: [] });
      setSaving(false);
    }
  }, [open, fixedMeetingId, initialText, initialPriority, initialProjectsKey]);

  const dirty = todoDraftChanged(values, initial) || (projectsOn && !sameIds(projects, initialProjectIds ?? []));
  const meetingOptions: MeetingOption[] | null = fixedMeetingId
    ? null
    : (meetings.data?.items ?? []).map((meeting) => ({ value: meeting.id, label: meeting.startedAt ? `${meeting.title} — ${formatDateOnly(meeting.startedAt.slice(0, 10))}` : meeting.title }));

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const checked = validate(values);
    if (checked.summary.length > 0) {
      setErrors(checked);
      window.setTimeout(() => summaryRef.current?.querySelector<HTMLElement>('[role="alert"]')?.focus(), 0);
      return;
    }
    const body: CreateTodoRequest = {
      text: values.text.trim(),
      dueDate: values.dueDate === '' ? null : values.dueDate,
      priority: values.priority,
      meetingId: values.meetingId === '' ? null : values.meetingId,
      // Why only with a choice: the route refuses the field under the floor, and an empty set files nothing.
      ...(projectsOn && projects.length > 0 ? { projectIds: projects } : {}),
    };
    setSaving(true);
    const result = await api.post<TodoDto>('/api/todos', body);
    setSaving(false);
    if (!result.ok) {
      setErrors(fromFailure(result));
      return;
    }
    onCreated(result.data);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Add a to-do"
      description={initial
        ? `Filled in from the suggestion.${fixedMeetingId ? ' The to-do is linked to this meeting.' : ''}`
        : fixedMeetingId ? 'The to-do is linked to this meeting.' : undefined}
      size="md"
      initialFocus="first"
      unsavedChanges={dirty && !saving}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" type="submit" form="todo-add-form" loading={saving}>Add a to-do</Button>
        </>
      }
    >
      <form id="todo-add-form" onSubmit={(event) => void submit(event)} noValidate ref={summaryRef} className="stack stack--3">
        <FormErrorSummary errors={errors.summary} />
        <TodoFields
          values={values}
          errors={errors}
          onChange={(next) => setValues((current) => ({ ...current, ...next }))}
          meetingOptions={meetingOptions}
          meetingsLoading={!fixedMeetingId && meetings.status === 'loading'}
        />
        {projectsOn && open ? <ProjectCheckboxes selected={projects} onChange={setProjects} disabled={saving} /> : null}
      </form>
    </Dialog>
  );
}
